/** <video-controls>: custom play/pause, sound and draggable progress controls for a section video — generic through the `scope` and `videos` selectors, rendered OUTSIDE the section's clickable wrapper so no <button> sits inside an anchor, applying actions to every video (a banner has a desktop and a mobile slot) while reading state from the visible one, and exposing data-playing/data-muted plus --video-progress for the CSS. */
(function () {
  if (customElements.get("video-controls")) return;

  const SEEK_STEP = 5; // secondes, navigation clavier

  class VideoControls extends HTMLElement {
    connectedCallback() {
      const scopeSelector = this.getAttribute("scope");
      const videoSelector = this.getAttribute("videos") || "video";
      const scope = (scopeSelector && this.closest(scopeSelector)) || document;

      this.videos = Array.from(scope.querySelectorAll(videoSelector));
      if (!this.videos.length) {
        this.hidden = true;
        return;
      }

      this.playBtn = this.querySelector("[data-video-toggle]");
      this.soundBtn = this.querySelector("[data-video-sound]");
      this.track = this.querySelector("[data-video-progress]");

      // Driving the controls must never follow a link wrapping the media. They are rendered outside that anchor today, so nothing bubbles into it, but the guarantee should not rest on the markup: cancelling the default action here neutralises the anchor even if the controls end up inside one. Propagation is left alone so document-level listeners still see the click.
      this.addEventListener("click", (event) => event.preventDefault());

      if (this.playBtn) this.playBtn.addEventListener("click", () => this.#togglePlay());
      if (this.soundBtn) this.soundBtn.addEventListener("click", () => this.#toggleSound());

      if (this.track) {
        this.track.addEventListener("pointerdown", (e) => this.#onPointerDown(e));
        this.track.addEventListener("pointermove", (e) => this.#onPointerMove(e));
        this.track.addEventListener("pointerup", () => this.#endDrag());
        this.track.addEventListener("pointercancel", () => this.#endDrag());
        this.track.addEventListener("keydown", (e) => this.#onKeydown(e));
      }

      // L'affichage suit l'état réel des vidéos : il reste juste même si
      // l'autoplay est refusé par le navigateur ou si le son change ailleurs.
      this.videos.forEach((video) => {
        video.addEventListener("play", () => this.#render());
        video.addEventListener("pause", () => this.#render());
        video.addEventListener("volumechange", () => this.#render());
        video.addEventListener("timeupdate", () => this.#render());
        video.addEventListener("loadedmetadata", () => this.#render());
      });

      this.#render();
    }

    /** Vidéo affichée (un autre slot peut être en display:none selon le breakpoint). */
    #video() {
      return this.videos.find((v) => v.offsetParent !== null) || this.videos[0];
    }

    /** play() renvoie une promesse rejetée si le navigateur refuse la lecture. */
    #play(video) {
      const played = video.play();
      if (played) played.catch(() => {});
    }

    #togglePlay() {
      const play = this.#video().paused;
      this.videos.forEach((video) => (play ? this.#play(video) : video.pause()));
    }

    #toggleSound() {
      const unmute = this.#video().muted;
      this.videos.forEach((video) => {
        video.muted = !unmute;
        if (!unmute) return;
        // Un volume à 0 rendrait le démutage inaudible ; iOS peut aussi
        // suspendre la lecture au démutage, d'où le play().
        if (video.volume === 0) video.volume = 1;
        this.#play(video);
      });
    }

    /** Reflète l'état de la vidéo visible dans les contrôles. */
    #render() {
      const video = this.#video();

      this.dataset.playing = video.paused ? "false" : "true";
      this.dataset.muted = video.muted ? "true" : "false";
      if (this.playBtn) this.playBtn.setAttribute("aria-pressed", video.paused ? "false" : "true");
      if (this.soundBtn) this.soundBtn.setAttribute("aria-pressed", video.muted ? "false" : "true");

      if (!this.track) return;
      const percent = video.duration > 0 ? (video.currentTime / video.duration) * 100 : 0;
      this.track.style.setProperty("--video-progress", percent + "%");
      this.track.setAttribute("aria-valuenow", Math.round(percent));
    }

    /** Déplace la lecture (tous les slots restent synchrones). */
    #seek(time) {
      this.videos.forEach((video) => {
        if (video.duration > 0) video.currentTime = Math.min(Math.max(time, 0), video.duration);
      });
      this.#render();
    }

    #onPointerDown(event) {
      const video = this.#video();
      if (!(video.duration > 0)) return;
      event.preventDefault(); // pas de sélection de texte ni de drag d'image
      this.dragging = true;
      this.track.classList.add("is-dragging");
      if (this.track.setPointerCapture) this.track.setPointerCapture(event.pointerId);
      this.#seekToPointer(event);
    }

    #onPointerMove(event) {
      if (this.dragging) this.#seekToPointer(event);
    }

    #endDrag() {
      this.dragging = false;
      this.track.classList.remove("is-dragging");
    }

    /** Position horizontale du pointeur sur la barre → temps de la vidéo. */
    #seekToPointer(event) {
      const rect = this.track.getBoundingClientRect();
      if (!rect.width) return;
      const ratio = (event.clientX - rect.left) / rect.width;
      this.#seek(ratio * this.#video().duration);
    }

    #onKeydown(event) {
      const video = this.#video();
      if (!(video.duration > 0)) return;

      const steps = {
        ArrowLeft: video.currentTime - SEEK_STEP,
        ArrowRight: video.currentTime + SEEK_STEP,
        Home: 0,
        End: video.duration,
      };
      if (!(event.key in steps)) return;

      event.preventDefault();
      this.#seek(steps[event.key]);
    }
  }

  customElements.define("video-controls", VideoControls);
})();
